import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ChevronRight, FileText, Shield, CheckCircle, AlertTriangle, Loader2, Play, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

// Video URL - Update this with your actual video
const VIDEO_URL = "https://www.youtube.com/embed/VIDEO_ID_HERE";

const Reclassification101 = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    parentFirstName: '',
    parentLastName: '',
    email: '',
    studentGrade: '',
    recruitmentGoal: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.parentFirstName || !formData.parentLastName || !formData.email || !formData.studentGrade || !formData.recruitmentGoal) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await api.post('/academy/reclassification-lead', formData);
      setSubmitted(true);
      toast.success('Audit sent! Check your email.');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Thank You / Confirmation Screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-white text-black">
        {/* Header */}
        <header className="bg-black py-4 px-6">
          <div className="container mx-auto flex items-center justify-between">
            <img 
              src="/images/scholar-parent-logo-new.png" 
              alt="Scholar Parent Academy" 
              className="h-12"
            />
            <nav className="hidden md:flex items-center gap-6">
              <a href="/academy" className="text-white hover:text-purple-400 transition-colors font-medium">Academy</a>
              <a href="/academy/diagnostic" className="text-white hover:text-purple-400 transition-colors font-medium">The Combine</a>
            </nav>
          </div>
        </header>

        {/* Confirmation Content */}
        <div className="container mx-auto px-6 py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">
              AUDIT RECEIVED.
            </h1>
            <h2 className="text-2xl md:text-3xl font-black text-purple-600 mb-6">
              NOW, GET THE DATA.
            </h2>
            
            <p className="text-gray-600 text-lg mb-8 max-w-lg mx-auto">
              Your Executive Audit is on its way to <span className="font-bold">{formData.email}</span>. 
              While you wait, discover your athlete's true Power Score.
            </p>

            <Button
              onClick={() => navigate('/academy/parent-diagnostic')}
              className="bg-purple-600 hover:bg-purple-700 text-white font-black text-lg px-10 py-6 rounded-none"
              data-testid="enter-combine-btn"
            >
              ENTER THE COMBINE
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>

            <p className="text-sm text-gray-500 mt-6">
              The Combine is your household's diagnostic tool for academic and athletic readiness.
            </p>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-black text-white py-8 mt-auto">
          <div className="container mx-auto px-6 text-center">
            <p className="text-gray-400 text-sm">
              "Where the Field Meets the Front Office."
            </p>
            <p className="text-gray-600 text-xs mt-2">
              © {new Date().getFullYear()} PRSVR Scholar Athlete Academy. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  // Main Landing Page
  return (
    <div className="min-h-screen bg-white text-black">
      {/* Header */}
      <header className="bg-black py-4 px-6 sticky top-0 z-50">
        <div className="container mx-auto flex items-center justify-between">
          <img 
            src="/images/scholar-parent-logo-new.png" 
            alt="Scholar Parent Academy" 
            className="h-12"
          />
          <nav className="hidden md:flex items-center gap-6">
            <a href="/academy" className="text-white hover:text-purple-400 transition-colors font-medium">Academy</a>
            <a href="/academy/diagnostic" className="text-white hover:text-purple-400 transition-colors font-medium">The Combine</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-black text-white py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="max-w-xl">
              <p className="text-purple-400 uppercase tracking-widest text-sm font-bold mb-4">
                Scholar Parent Academy
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-none mb-6 tracking-tight">
                RECLASSIFICATION 101:
                <br />
                <span className="text-purple-400">THE EXECUTIVE AUDIT</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                A Household Executive's guide to the academic, athletic, and emotional realities of the "Extra Year."
              </p>
              <Button
                onClick={() => setShowVideo(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-black text-lg px-8 py-6 rounded-none group"
                data-testid="watch-video-btn"
              >
                <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                WATCH THE BREAKDOWN
              </Button>
            </div>
            
            {/* Right - Video Thumbnail */}
            <div 
              className="relative cursor-pointer group"
              onClick={() => setShowVideo(true)}
            >
              <div className="aspect-video bg-gradient-to-br from-purple-900/50 to-black rounded-lg overflow-hidden border-2 border-purple-500/30 group-hover:border-purple-500 transition-colors">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl shadow-purple-600/50">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white font-bold text-lg">The Reclassification Decision</p>
                  <p className="text-gray-400 text-sm">3 min watch • Executive Director Breakdown</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl mx-4">
            <button
              onClick={() => setShowVideo(false)}
              className="absolute -top-12 right-0 text-white hover:text-purple-400 transition-colors"
              data-testid="close-video-btn"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <iframe
                src={VIDEO_URL + "?autoplay=1"}
                title="Reclassification 101 Video"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="text-center text-gray-400 text-sm mt-4">
              Press ESC or click X to close
            </p>
          </div>
        </div>
      )}

      {/* Main Content - Split Layout */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
            
            {/* Left Column - Content */}
            <div className="space-y-12">
              {/* Section 1: The Why */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="text-2xl font-black uppercase tracking-tight">Risk Management</h2>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Reclassification is a business decision. This guide audits the risks to your athlete's 
                  <span className="font-bold text-purple-600"> Power Score</span> before you commit to a new path.
                </p>
              </div>

              {/* Section 2: What's Inside */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="text-2xl font-black uppercase tracking-tight">What's Inside</h2>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-bold">The "Red-Shirt" Reality:</span>
                      <span className="text-gray-600"> Academic eligibility vs. Athletic age.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-bold">Record-Keeping Risks:</span>
                      <span className="text-gray-600"> Critical considerations for private school transitions.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-bold">The Go/No-Go Indicator:</span>
                      <span className="text-gray-600"> A checklist to guide your final decision.</span>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Visual Element */}
              <div className="bg-gray-50 p-6 border-l-4 border-purple-600">
                <p className="text-gray-600 italic">
                  "If a parent is worried about reclassification, this guide will lead them to realize they need to know their athlete's Power Score first."
                </p>
                <p className="text-purple-600 font-bold mt-2 text-sm">— Executive Director, SAA</p>
              </div>
            </div>

            {/* Right Column - Lead Capture Form */}
            <div className="sticky top-24">
              <Card className="border-2 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] bg-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-black mb-2 text-center">GET THE AUDIT</h3>
                  <p className="text-gray-600 text-center mb-8">
                    Free PDF delivered instantly to your inbox.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Parent First Name */}
                    <div>
                      <Label htmlFor="parentFirstName" className="text-sm font-bold uppercase tracking-wider">
                        Parent First Name *
                      </Label>
                      <Input
                        id="parentFirstName"
                        type="text"
                        value={formData.parentFirstName}
                        onChange={(e) => setFormData({...formData, parentFirstName: e.target.value})}
                        className="mt-2 border-2 border-gray-300 rounded-none h-12 focus:border-purple-600"
                        placeholder="First name"
                        data-testid="parent-first-name-input"
                        required
                      />
                    </div>

                    {/* Parent Last Name */}
                    <div>
                      <Label htmlFor="parentLastName" className="text-sm font-bold uppercase tracking-wider">
                        Parent Last Name *
                      </Label>
                      <Input
                        id="parentLastName"
                        type="text"
                        value={formData.parentLastName}
                        onChange={(e) => setFormData({...formData, parentLastName: e.target.value})}
                        className="mt-2 border-2 border-gray-300 rounded-none h-12 focus:border-purple-600"
                        placeholder="Last name"
                        data-testid="parent-last-name-input"
                        required
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <Label htmlFor="email" className="text-sm font-bold uppercase tracking-wider">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="mt-2 border-2 border-gray-300 rounded-none h-12 focus:border-purple-600"
                        placeholder="you@example.com"
                        data-testid="email-input"
                        required
                      />
                    </div>

                    {/* Student Grade */}
                    <div>
                      <Label htmlFor="studentGrade" className="text-sm font-bold uppercase tracking-wider">
                        Student Current Grade *
                      </Label>
                      <Select 
                        value={formData.studentGrade} 
                        onValueChange={(value) => setFormData({...formData, studentGrade: value})}
                      >
                        <SelectTrigger 
                          className="mt-2 border-2 border-gray-300 rounded-none h-12 focus:border-purple-600"
                          data-testid="student-grade-select"
                        >
                          <SelectValue placeholder="Select grade" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5th">5th Grade</SelectItem>
                          <SelectItem value="6th">6th Grade</SelectItem>
                          <SelectItem value="7th">7th Grade</SelectItem>
                          <SelectItem value="8th">8th Grade</SelectItem>
                          <SelectItem value="9th">9th Grade</SelectItem>
                          <SelectItem value="10th">10th Grade</SelectItem>
                          <SelectItem value="11th">11th Grade</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Recruitment Goal */}
                    <div>
                      <Label htmlFor="recruitmentGoal" className="text-sm font-bold uppercase tracking-wider">
                        Current Recruitment Goal? *
                      </Label>
                      <Select 
                        value={formData.recruitmentGoal} 
                        onValueChange={(value) => setFormData({...formData, recruitmentGoal: value})}
                      >
                        <SelectTrigger 
                          className="mt-2 border-2 border-gray-300 rounded-none h-12 focus:border-purple-600"
                          data-testid="recruitment-goal-select"
                        >
                          <SelectValue placeholder="Select goal" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AAU">AAU / Travel Ball</SelectItem>
                          <SelectItem value="Private School">Private School</SelectItem>
                          <SelectItem value="Collegiate">Collegiate</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-black text-lg py-6 rounded-none mt-6"
                      data-testid="submit-audit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          SENDING...
                        </>
                      ) : (
                        <>
                          👉 SEND ME THE AUDIT
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="text-xs text-gray-500 text-center mt-4">
                    We respect your privacy. No spam, ever.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-8">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">
            "Where the Field Meets the Front Office."
          </p>
          <p className="text-gray-600 text-xs mt-2">
            © {new Date().getFullYear()} PRSVR Scholar Athlete Academy. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Reclassification101;
